import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { haveSameProviders } from '#pplx/frontend/composer-sdk/internal/typeahead/arbitration.js';
import { $getCommandMenuMarker, $insertCommandMenuMarkerAtSelection, $removeCommandMenuMarker, COMMAND_MENU_MARKER_UPDATE_TAG, } from '#pplx/frontend/composer-sdk/internal/typeahead/commandMenuMarker.js';
import { $deleteEmptyCommandMenuQuery, $expandedSelectionOverlapsQuery, $getActiveCommandMenuQuery, $isSelectionAtActiveQueryEnd, } from '#pplx/frontend/composer-sdk/internal/typeahead/commandMenuQuery.js';
import { makeMarkerEditorApi } from '#pplx/frontend/composer-sdk/internal/typeahead/editorApi.js';
import { useDispatchTypeaheadSelection, useTypeaheadListNavigation, } from '#pplx/frontend/composer-sdk/internal/typeahead/useTypeaheadListNavigation.js';
import { $getRoot, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW, DELETE_CHARACTER_COMMAND, HISTORIC_TAG, HISTORY_MERGE_TAG, KEY_BACKSPACE_COMMAND, KEY_ESCAPE_COMMAND, } from 'lexical';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
function $shouldShowPlaceholder(query, visibility) {
    if (query.length !== 0)
        return false;
    switch (visibility) {
        case 'emptyQuery':
            return true;
        case 'emptyEditor':
            return $getRoot().getTextContentSize() === 0;
        default:
            return assertUnreachableValue(visibility);
    }
}
/**
 * Owns the marker-anchored path behind the controlled command menu. The open
 * state lives in the editor as an invisible marker node (see
 * `commandMenuMarker.ts`): opening inserts it at the caret, the update listener
 * derives the query from the text before it, and any close path — Escape,
 * backspace on an empty query, the caret leaving the query, a selection commit,
 * or the surface flipping `open` off — removes it. No visible trigger character
 * is involved, unlike the Lexical path's typed `/`.
 */
export function useMarkerAnchoredCommandMenu({ editor, providers, enabled, placeholder, placeholderVisibility, engine, state, reset, onSelectionHandoff, }) {
    const [active, setActive] = useState(false);
    const activeRef = useRef(false);
    const providersRef = useRef(EMPTY_ARRAY);
    const lastQueryRef = useRef(null);
    const commandMenuProviders = useMemo(() => providers.filter((provider) => provider.display === 'commandMenu'), [providers]);
    const commandMenuProvidersRef = useRef(commandMenuProviders);
    commandMenuProvidersRef.current = commandMenuProviders;
    // Caller-opened command menus retain wrapping unless their provider opts into another policy.
    const { selectedIndex, selectedIndexRef, rowsRef, selectRowRef, onHighlight, onArrowKey, resetSelection, } = useTypeaheadListNavigation({
        editor,
        enabled,
        activeRef,
        state,
        initialSelection: providersRef.current[0]?.initialSelection,
        wrapNavigation: true,
        navigation: providersRef.current[0]?.navigation,
    });
    const deactivate = useCallback(() => {
        if (!activeRef.current) {
            return;
        }
        activeRef.current = false;
        setActive(false);
        resetSelection();
        lastQueryRef.current = null;
        reset();
    }, [reset, resetSelection]);
    const activate = useCallback((query) => {
        const selected = commandMenuProvidersRef.current;
        if (selected.length === 0) {
            return;
        }
        if (activeRef.current && lastQueryRef.current === query) {
            return;
        }
        providersRef.current = selected;
        lastQueryRef.current = query;
        if (!activeRef.current) {
            resetSelection();
        }
        activeRef.current = true;
        setActive(true);
        engine.run(query, selected);
    }, [engine, resetSelection]);
    // Marker writes carry `HISTORY_MERGE_TAG`: opening or closing the menu is UI
    // state, not an edit, so it must not become an undo step of its own.
    const openMenu = useCallback(() => {
        if (!enabled || commandMenuProvidersRef.current.length === 0) {
            return;
        }
        editor.update(() => {
            $insertCommandMenuMarkerAtSelection(placeholder);
            // Decide in the insertion transaction so a hidden hint cannot flash before the listener.
            const activeQuery = $getActiveCommandMenuQuery();
            if (activeQuery !== null) {
                activeQuery.marker.setShowPlaceholder($shouldShowPlaceholder(activeQuery.query, placeholderVisibility));
            }
        }, { tag: HISTORY_MERGE_TAG });
        // Marker insertion set the Lexical selection; make sure DOM focus follows so
        // typing filters immediately (the open gesture is usually a button click).
        editor.getRootElement()?.focus({ preventScroll: true });
        editor.focus();
    }, [editor, enabled, placeholder, placeholderVisibility]);
    const closeMenu = useCallback(() => {
        editor.update(() => {
            $removeCommandMenuMarker();
        }, { tag: HISTORY_MERGE_TAG });
        deactivate();
    }, [editor, deactivate]);
    // Providers can change while the menu is open (the consumer rebuilding or
    // removing its plugins). Regenerated providers re-run the engine so rows and
    // selection handlers track them instead of the activation-time snapshot; no
    // providers left closes the menu rather than serving stale rows.
    useEffect(() => {
        if (!activeRef.current) {
            return;
        }
        if (commandMenuProviders.length === 0) {
            closeMenu();
            return;
        }
        if (lastQueryRef.current === null ||
            haveSameProviders(providersRef.current, commandMenuProviders)) {
            return;
        }
        providersRef.current = commandMenuProviders;
        engine.run(lastQueryRef.current, commandMenuProviders);
    }, [commandMenuProviders, engine, closeMenu]);
    // The overlay renders nothing for `error`, so an errored run must close the
    // path (removing the marker and reporting the close) — otherwise a controlled
    // consumer latches open with no visible menu. Reopening retries the fetch.
    useEffect(() => {
        if (activeRef.current && state.status === 'error') {
            closeMenu();
        }
    }, [state, closeMenu]);
    // The host unmounts this plugin with its last provider; the marker must not
    // outlive it (a stranded marker keeps its ghost hint visible in the draft
    // and rides into the next submission's serialized state).
    useEffect(() => {
        return () => {
            editor.update(() => {
                $removeCommandMenuMarker();
            }, { tag: [COMMAND_MENU_MARKER_UPDATE_TAG, HISTORY_MERGE_TAG] });
        };
    }, [editor]);
    // Removing the config closes and cleans up an open menu.
    useEffect(() => {
        if (enabled) {
            return;
        }
        editor.update(() => {
            $removeCommandMenuMarker();
        }, { tag: [COMMAND_MENU_MARKER_UPDATE_TAG, HISTORY_MERGE_TAG] });
        deactivate();
    }, [editor, enabled, deactivate]);
    // Derive the menu's state from the editor on every (non-bookkeeping) update:
    // marker present and the caret at the query's end keeps it open with that
    // query; anything else removes the marker. Reads collect decisions first;
    // mutations run in a follow-up tagged update, matching the legacy plugin.
    useEffect(() => {
        if (!enabled) {
            return;
        }
        return editor.registerUpdateListener(({ editorState, tags }) => {
            if (tags.has(COMMAND_MENU_MARKER_UPDATE_TAG)) {
                return;
            }
            // Undo/redo restore whole-state snapshots, which can resurrect a marker
            // that a close already removed. The marker is transient UI state, not
            // content, so strip it instead of activating — undo must never reopen
            // the menu or report a spurious `onOpenChange(true)`.
            if (tags.has(HISTORIC_TAG)) {
                const hasRestoredMarker = editorState.read(() => $getCommandMenuMarker() !== null);
                if (hasRestoredMarker) {
                    editor.update(() => {
                        $removeCommandMenuMarker();
                    }, { tag: [COMMAND_MENU_MARKER_UPDATE_TAG, HISTORY_MERGE_TAG] });
                }
                deactivate();
                return;
            }
            let hasMarker = false;
            let nextQuery = null;
            let shouldRemoveMarker = false;
            let shouldShowPlaceholder;
            editorState.read(() => {
                hasMarker = $getCommandMenuMarker() !== null;
                if (!hasMarker) {
                    return;
                }
                const activeQuery = $getActiveCommandMenuQuery();
                if (!activeQuery ||
                    (!$isSelectionAtActiveQueryEnd(activeQuery) &&
                        !$expandedSelectionOverlapsQuery(activeQuery))) {
                    // A structurally invalid marker, or the selection left the query.
                    shouldRemoveMarker = true;
                    return;
                }
                nextQuery = activeQuery.query;
                shouldShowPlaceholder = $shouldShowPlaceholder(activeQuery.query, placeholderVisibility);
            });
            if (!hasMarker) {
                deactivate();
                return;
            }
            if (shouldRemoveMarker) {
                deactivate();
                editor.update(() => {
                    $removeCommandMenuMarker();
                }, { tag: [COMMAND_MENU_MARKER_UPDATE_TAG, HISTORY_MERGE_TAG] });
                return;
            }
            if (nextQuery !== null) {
                activate(nextQuery);
            }
            editor.update(() => {
                const activeQuery = $getActiveCommandMenuQuery();
                if (activeQuery && activeQuery.marker.getShowPlaceholder() !== shouldShowPlaceholder) {
                    activeQuery.marker.setShowPlaceholder(Boolean(shouldShowPlaceholder));
                }
            }, { tag: [COMMAND_MENU_MARKER_UPDATE_TAG, HISTORY_MERGE_TAG] });
        });
    }, [editor, enabled, activate, deactivate, placeholderVisibility]);
    const selectRow = useDispatchTypeaheadSelection({
        editor,
        onSelection: (index, selectionMethod, source) => {
            const row = rowsRef.current[index];
            if (row?.item.disabled === true)
                return null;
            const provider = row ? providersRef.current[row.providerIndex] : undefined;
            if (!row || !provider)
                return { outcome: undefined, markerKey: undefined };
            const markerKey = editor.getEditorState().read(() => $getCommandMenuMarker()?.getKey());
            if (source === 'sidePanel' && markerKey === undefined)
                return null;
            return {
                markerKey,
                outcome: provider.onSelect(row.item, makeMarkerEditorApi(editor), Object.freeze({
                    selectionMethod,
                    ...(source === undefined ? {} : { source }),
                    flatIndex: index,
                })),
            };
        },
        onClose: closeMenu,
        onHandoff: ({ markerKey }) => {
            onSelectionHandoff();
            const marker = $getCommandMenuMarker();
            if (marker?.getKey() === markerKey)
                marker?.remove();
        },
    });
    selectRowRef.current = selectRow;
    const selectPointerRow = useCallback((index) => selectRow(index, 'pointer'), [selectRow]);
    const selectSidePanel = useCallback(({ row, provider, flatIndex }) => {
        // Ignore a saved panel action if the menu closed or its highlight, results or provider
        // changed. Returning without selecting prevents an old control from choosing the wrong item.
        if (!activeRef.current ||
            selectedIndexRef.current !== flatIndex ||
            rowsRef.current[flatIndex] !== row ||
            providersRef.current[row.providerIndex] !== provider) {
            return;
        }
        selectRow(flatIndex, 'pointer', 'sidePanel');
    }, [rowsRef, selectedIndexRef, selectRow]);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        const removeEscape = editor.registerCommand(KEY_ESCAPE_COMMAND, (event) => {
            if (!activeRef.current) {
                return false;
            }
            // Mark the event consumed so host layers honoring `defaultPrevented`
            // (dialogs, popovers) don't also close on the same Escape — one layer
            // per press.
            event?.preventDefault();
            closeMenu();
            return true;
        }, COMMAND_PRIORITY_LOW);
        // Backspace on an empty query closes the menu instead of deleting draft text
        // before it. HIGH priority so it wins over the default character deletion.
        const removeBackspace = editor.registerCommand(KEY_BACKSPACE_COMMAND, (event) => {
            if (!activeRef.current || !$deleteEmptyCommandMenuQuery()) {
                return false;
            }
            // Returning true skips Lexical's default backspace handler — and with it
            // the `preventDefault` that suppresses the browser's own contenteditable
            // deletion, so it must happen here or the draft loses a character.
            // `stopPropagation` keeps host keydown listeners from acting on a
            // backspace the menu already consumed as its close gesture.
            event.preventDefault();
            event.stopPropagation();
            return true;
        }, COMMAND_PRIORITY_HIGH);
        const removeDeleteCharacter = editor.registerCommand(DELETE_CHARACTER_COMMAND, (isBackward) => {
            if (!activeRef.current || !isBackward) {
                return false;
            }
            return $deleteEmptyCommandMenuQuery();
        }, COMMAND_PRIORITY_HIGH);
        return () => {
            removeEscape();
            removeBackspace();
            removeDeleteCharacter();
        };
    }, [editor, enabled, closeMenu]);
    return {
        active,
        activeRef,
        selectedIndex,
        onHighlight,
        onArrowKey,
        onSelect: selectPointerRow,
        onSelectSidePanel: selectSidePanel,
        provider: commandMenuProviders[0],
        providersRef,
        openMenu,
        closeMenu,
    };
}
//# sourceMappingURL=useMarkerAnchoredCommandMenu.js.map